import React from 'react'
import { HistoryEvent } from '../events/event.types'

import { BuildingPermitDetail } from '../../../organisms/BuildingPermitDetail'
import { TransactionDetail as TransactionDetailOrganism } from '../../../organisms/TransactionDetail'
import { ListingDetail as ListingDetailOrganism } from '../../../organisms/ListingDetail'
import { ListingDetail } from './ListingDetail'
import {
  DetailPanel,
  DetailHeader,
  DetailHeaderContent,
  BackButton,
  DetailTitle,
  DetailContent,
} from './EventDetail.styles'

interface EventDetailPanelProps {
  event: HistoryEvent
  onClose: () => void
  formatPrice?: (price: number) => string
  formatPricePerSqm?: (price: number) => string
  formatDate?: (date: string) => string
  translations?: any
  loadTransaction?: (transactionId: number | string) => Promise<any>
  loadListing?: (listingId: number) => Promise<any>
  getParticipantsByRoleGrouped?: (permit: any) => Record<string, any[]>
}

export const EventDetailPanel: React.FC<EventDetailPanelProps> = ({
  event,
  onClose,
  formatPrice,
  formatPricePerSqm,
  formatDate,
  translations,
  loadTransaction,
  loadListing,
  getParticipantsByRoleGrouped,
}) => {
  const getEventTitle = () => {
    switch (event.type) {
      case 'permit':
        return translations?.permit?.title || 'Building Permit'
      case 'transaction':
        return translations?.transaction?.title || 'Property Transaction'
      case 'rental':
        return translations?.rental?.title || 'Rental Listing'
      case 'sale':
        return translations?.sale?.title || 'Sale Listing'
      default:
        return translations?.eventDetails || 'Event Details'
    }
  }

  // For permit events, render BuildingPermitDetail with its own panel
  if (event.type === 'permit') {
    return (
      <DetailPanel $isOpen={true}>
        <BuildingPermitDetail
          isOpen={true}
          permit={event}
          onClose={onClose}
          contained={true}
          disableResize={true}
          getParticipantsByRoleGrouped={getParticipantsByRoleGrouped}
        />
      </DetailPanel>
    )
  }

  // For transaction events, use full organism TransactionDetail
  if (event.type === 'transaction') {
    const transactionEvent = event as any
    return (
      <DetailPanel $isOpen={true}>
        <TransactionDetailOrganism
          isOpen={true}
          onClose={onClose}
          transactionId={transactionEvent.id || transactionEvent.transaction_id}
          loadTransaction={loadTransaction}
          contained={true}
          disableResize={true}
        />
      </DetailPanel>
    )
  }

  // For listing events, use full organism ListingDetail when listingId is available
  if (event.type === 'rental' || event.type === 'sale') {
    const listingEvent = event as any
    if (listingEvent.listingId && loadListing) {
      return (
        <DetailPanel $isOpen={true}>
          <ListingDetailOrganism
            isOpen={true}
            onClose={onClose}
            listingId={String(listingEvent.listingId)}
            loadListing={loadListing}
            contained={true}
            disableResize={true}
            hideFooterActions={true}
            hideMap={true}
          />
        </DetailPanel>
      )
    }

    // Fallback: use molecule detail for embedded events without listingId
    return (
      <DetailPanel $isOpen={true}>
        <DetailHeader>
          <DetailHeaderContent>
            <BackButton onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 12H5M5 12L12 19M5 12L12 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {translations?.back || 'Back'}
            </BackButton>
            <DetailTitle>{getEventTitle()}</DetailTitle>
          </DetailHeaderContent>
        </DetailHeader>
        <DetailContent>
          <ListingDetail
            event={listingEvent}
            formatPrice={formatPrice}
            formatPricePerSqm={formatPricePerSqm}
            formatDate={formatDate}
            translations={translations}
          />
        </DetailContent>
      </DetailPanel>
    )
  }

  return null
}
